import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  listDews,
  listServers,
  listImageDetails,
  listSecurityGroups,
  projectVolumeList,
  invoiceList,
  paymentHistoryList,
  creditHistoryList,
  listServerSnapshots,
  listVolumeSnapshots,
  listVolumeBackups,
  projectNetworkList,
  projectSubnetList,
  projectRouterList,
  projectRouterInterfaceList,
  projectPortList,
  listProjectFloatingIPs,
  listProjectLoadBalancers,
  auditLogList,
  projectListenerList,
  projectPoolList,
  projectHealthMonitorList,
  projectMemberList,
  listDBServers,
  listPartners,
  listPartnerDews,
  listPartnerResources,
  listPartnerWallet,
  listPartnerPriceLists,
} from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import { extractErrorMessage } from "../../../hooks/constants";
import { toast } from "react-toastify";

export function useListDews(
  projectId
) {
  const [listDew, setListDews] =
    useState({
      results: [],
    });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectId) {
      setListDews({
        results: [],
      });

      return;
    }

    let mounted = true;

    setListDews({
      results: [],
    });

    const fetchFlavours = async () => {
      try {
        const { payload } =
          await dispatch(
            listDews({
              navigate,
            })
          );

        if (mounted) {
          setListDews(
            payload || {
              results: [],
            }
          );
        }
      } catch (err) {
        if (mounted) {
          setListDews({
            results: [],
          });
        }

        console.error(
          'Error fetching flavours:',
          err
        );
      }
    };

    fetchFlavours();

    return () => {
      mounted = false;
    };
  }, [
    projectId,
    dispatch,
    navigate,
  ]);

  return listDew;
}
// Updated partner hooks with conditional logic

export function useListPartnerDews(data) {
  const [listDew, setListDews] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!data) {
      setListDews([]);
      return;
    }

    const fetchListOfPartnerDew = async () => {
      try {
        const { payload } = await dispatch(listPartnerDews(data));
        setListDews(payload);
      } catch (err) {
        console.error('Error fetching partner dews:', err);
        setListDews([]);
      }
    };
    fetchListOfPartnerDew();
  }, [dispatch, navigate, data]);

  return listDew;
}

export function useListPartnerResources(data) {
  const [listResources, setListResources] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // If data is null or falsy, don't make the request
    if (!data) {
      setListResources([]);
      return;
    }

    const fetchListOfPartnerResources = async () => {
      try {
        const { payload } = await dispatch(listPartnerResources(data));
        setListResources(payload || []);
      } catch (err) {
        console.error('Error fetching partner resources:', err);
        setListResources([]);
      }
    };
    fetchListOfPartnerResources();
  }, [dispatch, navigate, data]);

  return listResources;
}

export function useListPartnerWallet(data) {
  const [listWallet, setListWallet] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // If data is null or falsy, don't make the request
    if (!data) {
      setListWallet([]);
      return;
    }

    const fetchListOfPartnerWallet = async () => {
      try {
        const { payload } = await dispatch(listPartnerWallet(data));
        setListWallet(payload || []);
      } catch (err) {
        console.error('Error fetching partner wallet:', err);
        setListWallet([]);
      }
    };
    fetchListOfPartnerWallet();
  }, [dispatch, navigate, data]);

  return listWallet;
}

export function useListPartnerPriceLists(data) {
  const [listPriceLists, setListPriceLists] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // If data is null or falsy, don't make the request
    if (!data) {
      setListPriceLists([]);
      return;
    }

    const fetchListOfPartnerPriceLists = async () => {
      try {
        const { payload } = await dispatch(listPartnerPriceLists(data));
        setListPriceLists(payload || []);
      } catch (err) {
        console.error('Error fetching partner price lists:', err);
        setListPriceLists([]);
      }
    };
    fetchListOfPartnerPriceLists();
  }, [dispatch, navigate, data]);

  return listPriceLists;
}


export function useListPartners(shouldFetch = true) {

  const [partners, setPartners] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!shouldFetch) {
      setPartners([]);
      return;
    }

    const fetchPartners = async () => {
      try {
        const { payload } = await dispatch(listPartners());
        if (payload && Array.isArray(payload)) {
          setPartners(payload);
        } else {
          console.warn("Failed to fetch partners or invalid response");
          setPartners([]);
        }
      } catch (err) {
        console.error('Error fetching partners:', err);
        setPartners([]);
      }
    };

    fetchPartners();
  }, [dispatch, shouldFetch]);

  return partners;

}

export function useServerList() {
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const [userServer, setUserServer] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedProjectId =
      selectedProject && selectedProject.id ? selectedProject.id : null;
    const fetchServers = async () => {
      try {
        const { payload } = await dispatch(listServers(selectedProjectId));
        setUserServer(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching servers:", err);
        toast.error(errorMsg, { theme: "colored" });
        setUserServer([]);
      }
    };
    fetchServers();
  }, [dispatch, selectedProject]);
  return userServer;

}

export function useDBServerList() {
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const [userDBServer, setUserDBServer] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedProjectId =
      selectedProject && selectedProject.id ? selectedProject.id : null;
    const fetchServers = async () => {
      try {
        const { payload } = await dispatch(listDBServers(selectedProjectId));
        setUserDBServer(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching database servers:", err);
        toast.error(errorMsg, { theme: "colored" });
        setUserDBServer([]);
      }
    };
    fetchServers();
  }, [dispatch, selectedProject]);
  return userDBServer;
}

export function useFloatingIPsList() {
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const [floatingIPs, setFloatingIPs] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedProjectId =
      selectedProject && selectedProject.id ? selectedProject.id : null;
    const fetchFloatingIPs = async () => {
      try {
        const { payload } = await dispatch(listProjectFloatingIPs(selectedProjectId));
        setFloatingIPs(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching floating IPs:", err);
        toast.error(errorMsg, { theme: "colored" });
        setFloatingIPs([]);
      }
    };
    fetchFloatingIPs();
  }, [dispatch, selectedProject]);
  return floatingIPs;
}


export function useLoadBalancersList() {
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const [loadBalancers, setLoadBalancers] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedProjectId =
      selectedProject && selectedProject.id ? selectedProject.id : null;
    const fetchLoadBalancers = async () => {
      try {
        const { payload } = await dispatch(listProjectLoadBalancers(selectedProjectId));
        setLoadBalancers(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching load balancers:", err);
        toast.error(errorMsg, { theme: "colored" });
        setLoadBalancers([]);
      }
    };
    fetchLoadBalancers();
  }, [dispatch, selectedProject]);
  return loadBalancers;
}


export function useServerSnapshotsList() {
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const [serverSnaphost, setServerSnaphost] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedProjectId =
      selectedProject && selectedProject.id ? selectedProject.id : null;
    const fetchServerSnapshots = async () => {
      try {
        const { payload } = await dispatch(listServerSnapshots(selectedProjectId));
        setServerSnaphost(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching server snapshots:", err);
        toast.error(errorMsg, { theme: "colored" });
        setServerSnaphost([]);
      }
    };
    fetchServerSnapshots();
  }, [dispatch, selectedProject]);
  return serverSnaphost;
}

export function useVolumeSnapshotsList() {
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const [volumeSnapshot, setVolumeSnapshot] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedProjectId =
      selectedProject && selectedProject.id ? selectedProject.id : null;
    const fetchVolumeSnapshots = async () => {
      try {
        const { payload } = await dispatch(listVolumeSnapshots(selectedProjectId));
        setVolumeSnapshot(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching volume snapshots:", err);
        toast.error(errorMsg, { theme: "colored" });
        setVolumeSnapshot([]);
      }
    };
    fetchVolumeSnapshots();
  }, [dispatch, selectedProject]);
  return volumeSnapshot;
}


export function useVolumeBackupsList() {
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const [volumeBackup, setVolumeBackup] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedProjectId =
      selectedProject && selectedProject.id ? selectedProject.id : null;
    const fetchVolumeBackups = async () => {
      try {
        const { payload } = await dispatch(listVolumeBackups(selectedProjectId));
        setVolumeBackup(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching volume backups:", err);
        toast.error(errorMsg, { theme: "colored" });
        setVolumeBackup([]);
      }
    };
    fetchVolumeBackups();
  }, [dispatch, selectedProject]);
  return volumeBackup;
}

export function useListSecurityGroups() {
  const [listGroups, setListGroups] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    const fetchSecurityGroups = async () => {
      try {
        const { payload } = await dispatch(listSecurityGroups({ navigate }));
        setListGroups(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching security groups:", err);
        toast.error(errorMsg, { theme: "colored" });
        setListGroups([]);
      }
    };
    fetchSecurityGroups();
  }, [dispatch, navigate]);
  return listGroups;
}


export function useListLocalImageDetails() {
  const [listLocalImageDetails, setListLocalImageDetails] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLocalImageDetails = async () => {
      try {
        const { payload } = await dispatch(listImageDetails({ navigate }));
        setListLocalImageDetails(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching image details:", err);
        toast.error(errorMsg, { theme: "colored" });
        setListLocalImageDetails([]);
      }
    };
    fetchLocalImageDetails();
  }, [dispatch, navigate]);
  return listLocalImageDetails;
}


export function extractDiskSize(diskSize) {
  if (!diskSize || typeof diskSize !== "string") {
    return null;
  }

  const match = diskSize.match(/(\d+(\.\d+)?)/);

  return match ? parseFloat(match[0]) : null;
}

export function useVolumeList(projectId) {
  const [volumeList, setVolumeList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchVolumes = async () => {
      try {
        const { payload } = await dispatch(projectVolumeList(projectId));
        setVolumeList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching volumes:", err);
        toast.error(errorMsg, { theme: "colored" });
        setVolumeList([]);
      }
    };
    fetchVolumes();
  }, [dispatch, projectId]);
  return volumeList;
}


export function useNetworkList(projectId) {
  const [networkList, setNetworkList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const { payload } = await dispatch(projectNetworkList(projectId));
        setNetworkList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching networks:", err);
        toast.error(errorMsg, { theme: "colored" });
        setNetworkList([]);
      }
    };
    fetchNetworks();
  }, [dispatch, projectId]);
  return networkList;
}

export function useSubnetList(projectId, params) {
  const [subnetList, setSubnetList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchSubnets = async () => {
      try {
        const { payload } = await dispatch(projectSubnetList({ projectId, params }));
        setSubnetList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching subnets:", err);
        toast.error(errorMsg, { theme: "colored" });
        setSubnetList([]);
      }
    };
    fetchSubnets();
  }, [dispatch, projectId, params]);
  return subnetList;
}


export function useListenerList(projectId, params) {
  const [listenerList, setListenerList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchListeners = async () => {
      try {
        const { payload } = await dispatch(projectListenerList({ projectId, params }));
        setListenerList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching listeners:", err);
        toast.error(errorMsg, { theme: "colored" });
        setListenerList([]);
      }
    };
    fetchListeners();
  }, [dispatch, projectId]);
  return listenerList;
}


export function useHealthMonitorList(projectId, poolId) {
  const [healthMonitorList, setHealthMonitorList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchHealthMonitors = async () => {
      try {
        const { payload } = await dispatch(projectHealthMonitorList({ projectId, poolId }));
        setHealthMonitorList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching health monitors:", err);
        toast.error(errorMsg, { theme: "colored" });
        setHealthMonitorList([]);
      }
    };
    fetchHealthMonitors();
  }, [dispatch, projectId, poolId]);
  return healthMonitorList;
}


export function useMemberList(projectId, poolId) {
  const [memberList, setMemberList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const { payload } = await dispatch(projectMemberList({ projectId, poolId }));
        setMemberList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching members:", err);
        toast.error(errorMsg, { theme: "colored" });
        setMemberList([]);
      }
    };
    fetchMembers();
  }, [dispatch, projectId]);
  return memberList;
}



export function usePoolList(projectId, params) {
  const [poolList, setPoolList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchPools = async () => {
      try {
        const { payload } = await dispatch(projectPoolList({ projectId, params }));
        setPoolList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching pools:", err);
        toast.error(errorMsg, { theme: "colored" });
        setPoolList([]);
      }
    };
    fetchPools();
  }, [dispatch, projectId]);
  return poolList;
}



export function usePortList(projectId, params) {
  const [portList, setPortList] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchPorts = async () => {
      try {

        const { payload } = await dispatch(projectPortList({ projectId, params }));
        setPortList(payload);
      } catch (err) {
        console.error('Error fetching ports:', err);
      }
    };
    fetchPorts();
  }, [dispatch, projectId, params]);

  return portList;
}


export function useRouterList(projectId) {
  const [routerList, setRouterList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchRouters = async () => {
      try {
        const { payload } = await dispatch(projectRouterList(projectId));
        setRouterList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching routers:", err);
        toast.error(errorMsg, { theme: "colored" });
        setRouterList([]);
      }
    };
    fetchRouters();
  }, [dispatch, projectId]);
  return routerList;
}

export function useRouterInterfaceList(projectId, data) {
  const [routerInterfaceList, setRouterInterfaceList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchRouterInterfaces = async () => {
      try {
        const { payload } = await dispatch(projectRouterInterfaceList({ projectId, data }));
        setRouterInterfaceList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching router interfaces:", err);
        toast.error(errorMsg, { theme: "colored" });
        setRouterInterfaceList([]);
      }
    };
    fetchRouterInterfaces();
  }, [dispatch, projectId, data]);
  return routerInterfaceList;
}




export function useInvoiceList() {
  const [invoicesList, setInvoicesList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const { payload } = await dispatch(invoiceList());
        setInvoicesList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching invoices:", err);
        toast.error(errorMsg, { theme: "colored" });
        setInvoicesList([]);
      }
    };
    fetchInvoices();
  }, [dispatch]);
  return invoicesList;
}

export function usePaymentHistoryList() {
  const [paymentHistoriesList, setPaymentHistoriesList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchPaymentHistories = async () => {
      try {
        const { payload } = await dispatch(paymentHistoryList());
        setPaymentHistoriesList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching payment histories:", err);
        toast.error(errorMsg, { theme: "colored" });
        setPaymentHistoriesList([]);
      }
    };
    fetchPaymentHistories();
  }, [dispatch]);
  return paymentHistoriesList;
}

export function useAuditLogList() {
  const [auditLogsList, setAuditLogsList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        const { payload } = await dispatch(auditLogList());
        setAuditLogsList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching audit logs:", err);
        toast.error(errorMsg, { theme: "colored" });
        setAuditLogsList([]);
      }
    };
    fetchAuditLogs();
  }, [dispatch]);
  return auditLogsList;
}

export function useCreditHistoryList() {
  const [creditHistoriesList, setCreditHistoriesList] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchCreditHistories = async () => {
      try {
        const { payload } = await dispatch(creditHistoryList());
        setCreditHistoriesList(payload);
      } catch (err) {
        const errorMsg = extractErrorMessage(err);
        console.error("Error fetching credit histories:", err);
        toast.error(errorMsg, { theme: "colored" });
        setCreditHistoriesList([]);
      }
    };
    fetchCreditHistories();
  }, [dispatch]);
  return creditHistoriesList;
}
